import React, { useMemo } from "react";
import { useAccountContext, useReactivateSubscripiton } from "./AccountContext";
import { BoxCard } from "./BoxCard";
import { ItemCard } from "./SubProductCard";
import { InactiveSubscriptions } from "./types";
import { productSelect } from "@/store";
import { useCustomer } from "../CustomerContext";
import { droppsProductToProductCardProduct } from "./helpers";

type InactiveSubscriptionsListProps = {
  onReactivate: (title: string) => void;
  requestInProgress: boolean;
};

const InactiveSubscriptionsList = ({
  onReactivate,
  requestInProgress,
}: InactiveSubscriptionsListProps) => {
  const customer = useCustomer();
  const { allItems } = useAccountContext();
  const inactiveItems = useMemo(() => {
    const lastYear = new Date(
      new Date().setFullYear(new Date().getFullYear() - 1)
    );
    const inactives = allItems.filter(
      (x) =>
        x.rechargeItem.status === "CANCELLED" &&
        x.rechargeItem.cancelled_at &&
        new Date(x.rechargeItem.cancelled_at) > lastYear &&
        productSelect
          .orderable(x.product, {
            bulk: customer?.isWholeSale ? "include" : "exclude",
            pos: "portal",
          })
          ?.variants.find((v) => v.id === x.variant.id)
    );
    inactives.sort((a, b) => b.rechargeItem.id - a.rechargeItem.id);
    return inactives;
  }, [allItems, customer]);

  if (inactiveItems.length == 0) {
    return null;
  }

  return (
    <div className="space-y-8">
      <h2 className="title text-center text-2xl md:text-left">
        Cancelled Subscriptions
      </h2>
      <ol className="space-y-8">
        {inactiveItems.map((sub) => (
          <InactiveSubscription
            key={sub.rechargeItem.id}
            item={sub}
            onReactivate={onReactivate}
            requestInProgress={requestInProgress}
          />
        ))}
      </ol>
    </div>
  );
};

const InactiveSubscription = ({
  item,
  onReactivate,
  requestInProgress,
}: {
  item: InactiveSubscriptions;
} & InactiveSubscriptionsListProps) => {
  const inactiveSubscriptionUpdater = useReactivateSubscripiton();
  const { rechargeItem: subscription, address, product, variant } = item;
  if (!subscription.cadence) {
    return null;
  }

  const productName = productSelect.name(item.product);
  const isLoading = inactiveSubscriptionUpdater.isLoading;
  const subscriptionActions = [
    {
      displayText: "reactivate",
      action: () =>
        inactiveSubscriptionUpdater.mutate(item.rechargeItem.id, {
          onSuccess: () => onReactivate(productName),
        }),
    },
  ];

  const productCardProduct = droppsProductToProductCardProduct({
    product,
    variant,
  });

  return (
    <li>
      <BoxCard address={address} data-address-id={address.id}>
        <ItemCard
          responsive={true}
          product={productCardProduct}
          price={subscription.price}
          shippingInfo={subscription.cadence}
          subscriptionActions={subscriptionActions}
          quantity={subscription.quantity}
          loading={isLoading}
          disabled={requestInProgress}
          data-subscription-id={subscription.id}
        />
      </BoxCard>
    </li>
  );
};

export { InactiveSubscriptionsList };
